import { Shell } from "../../components/Shell";
import { ErrorNotice, Loading, PageHead, Segmented, StatusBadge } from "../../components/ui";
import { developerApi } from "../../lib/api";
import { useQuery } from "../../lib/query";
import { Link, useSearchParam } from "../../lib/router";
import { useClient } from "../../lib/session";
import type { Env, Game } from "../../lib/types";
import { GameActivity } from "./Activity";
import { GameBanks } from "./Banks";
import { GameClock } from "./ClockTab";
import { GameCards } from "./Cards";
import { GameCurrencies } from "./Currencies";
import { GameFees } from "./Fees";
import { GameKeys } from "./Keys";
import { GameLoans } from "./Loans";
import { GameLiveBalances } from "./LiveBalances";
import { GameOverview } from "./Overview";
import { GameSettings } from "./GameSettings";
import { GameSavings } from "./Savings";
import { Sandbox } from "./Sandbox";
import { GameWebhooks } from "./Webhooks";

const TABS: { id: string; label: string; envAware: boolean; pro?: boolean }[] = [
  { id: "overview", label: "Overview", envAware: false },
  { id: "live", label: "Live balances", envAware: true, pro: true },
  { id: "keys", label: "API keys", envAware: true },
  { id: "banks", label: "Banks & limits", envAware: true },
  { id: "fees", label: "Fees & charges", envAware: true },
  { id: "cards", label: "Cards", envAware: true },
  { id: "savings", label: "Savings", envAware: true },
  { id: "loans", label: "Loans", envAware: true },
  { id: "currencies", label: "Currencies", envAware: true },
  { id: "clock", label: "Game clock", envAware: true },
  { id: "webhooks", label: "Webhooks", envAware: true },
  { id: "sandbox", label: "Sandbox playground", envAware: false },
  { id: "activity", label: "Activity", envAware: true },
  { id: "settings", label: "Settings", envAware: false },
];

export interface GameTabProps {
  game: Game;
  env: Env;
}

export function DevGame({ id, tab }: { id: string; tab: string }) {
  const api = developerApi(useClient());
  const q = useQuery(`dev:game:${id}`, () => api.game(id));
  const [envParam, setEnvParam] = useSearchParam("env");
  const env: Env = envParam === "production" ? "production" : "sandbox";
  const current = TABS.find((t) => t.id === tab) ?? TABS[0];
  const g = q.data;

  return (
    <Shell gameId={id} gameName={g?.name} crumbs={[{ label: "Your games", to: "/dev/games" }, { label: g?.name ?? "Game", to: `/dev/games/${id}` }, ...(current.id !== "overview" ? [{ label: current.label }] : [])]}>
      <ErrorNotice error={q.error} onRetry={q.reload} />
      {!g && q.loading && <Loading />}
      {g && (
        <>
          <PageHead
            title={g.name}
            badge={<StatusBadge status={g.status} />}
            actions={
              current.envAware ? (
                <Segmented<Env>
                  label="Environment"
                  value={env}
                  onChange={(v) => setEnvParam(v === "sandbox" ? "" : v)}
                  options={[{ value: "sandbox", label: "Sandbox" }, { value: "production", label: "Production" }]}
                />
              ) : undefined
            }
          />
          <nav className="tabs" aria-label="Game sections">
            {TABS.map((t) => (
              <Link key={t.id} to={`/dev/games/${id}${t.id === "overview" ? "" : `/${t.id}`}${t.envAware && env === "production" ? "?env=production" : ""}`} exact>
                {t.label}
                {t.pro && <span className="tab-pro">Pro</span>}
              </Link>
            ))}
          </nav>
          {g.status !== "active" && (
            <div className="notice warn" style={{ marginBottom: 20 }}>
              This game is suspended, so its API keys are refused.{" "}
              {current.id !== "settings" && <Link to={`/dev/games/${id}/settings`}>Reactivate it in Settings</Link>}
            </div>
          )}
          {env === "production" && current.envAware && current.id !== "live" && (
            <div className="notice warn" style={{ marginBottom: 20 }}>
              You're looking at production. Changes here affect real players.
            </div>
          )}
          {current.id === "overview" && <GameOverview game={g} env="sandbox" />}
          {current.id === "live" && <GameLiveBalances game={g} env={env} />}
          {current.id === "keys" && <GameKeys game={g} env={env} />}
          {current.id === "banks" && <GameBanks game={g} env={env} />}
          {current.id === "fees" && <GameFees game={g} env={env} />}
          {current.id === "cards" && <GameCards game={g} env={env} />}
          {current.id === "savings" && <GameSavings game={g} env={env} />}
          {current.id === "loans" && <GameLoans game={g} env={env} />}
          {current.id === "currencies" && <GameCurrencies game={g} env={env} />}
          {current.id === "clock" && <GameClock game={g} env={env} />}
          {current.id === "webhooks" && <GameWebhooks game={g} env={env} />}
          {current.id === "sandbox" && <Sandbox game={g} env="sandbox" />}
          {current.id === "activity" && <GameActivity game={g} env={env} />}
          {current.id === "settings" && <GameSettings key={g.updated_at} game={g} env="sandbox" />}
        </>
      )}
    </Shell>
  );
}
